import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  X, History, User as UserIcon, Clock, ArrowRight,
  Plus, CheckCircle2, RotateCcw, FilePlus, FileMinus, Pencil, Trash2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_META = {
  created:          { label: 'Created',          Icon: Plus,         tone: 'bg-indigo-100 text-indigo-700' },
  completed:        { label: 'Completed',        Icon: CheckCircle2, tone: 'bg-green-100 text-green-700'  },
  reopened:         { label: 'Re-opened',        Icon: RotateCcw,    tone: 'bg-amber-100 text-amber-700'  },
  updated:          { label: 'Updated',          Icon: Pencil,       tone: 'bg-blue-100 text-blue-700'    },
  document_added:   { label: 'Document Added',   Icon: FilePlus,     tone: 'bg-teal-100 text-teal-700'    },
  document_removed: { label: 'Document Removed', Icon: FileMinus,    tone: 'bg-rose-100 text-rose-700'    },
  deleted:          { label: 'Deleted',          Icon: Trash2,       tone: 'bg-red-100 text-red-700'      },
};

const fmtValue = (v) => {
  if (v === null || v === undefined || v === '') return <em className="text-gray-400">empty</em>;
  if (typeof v === 'object') return <code className="text-xs">{JSON.stringify(v)}</code>;
  const s = String(v);
  return s.length > 120 ? s.slice(0, 120) + '…' : s;
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

export default function ProjectHistoryModal({ open, onClose, projectId, projectName }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !projectId) return;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/projects/${projectId}/history`)
      .then((res) => setEntries(res.data?.data || []))
      .catch(() => setError('Failed to load project history'))
      .finally(() => setLoading(false));
  }, [open, projectId]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="project-history-modal"
    >
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between bg-gradient-to-r from-[#1a237e] to-[#283593] rounded-t-xl">
          <div className="flex items-center gap-2 text-white">
            <History size={20} />
            <div>
              <h2 className="text-lg font-bold">Project Timeline</h2>
              <p className="text-xs text-white/80">{projectName || projectId}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-white/80 hover:bg-white/10 rounded-lg"
            data-testid="project-history-close-btn"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {loading && <div className="text-center text-gray-500 py-8">Loading…</div>}
          {error && <div className="text-center text-red-600 py-8">{error}</div>}
          {!loading && !error && entries.length === 0 && (
            <div className="text-center text-gray-500 py-12" data-testid="project-history-empty">
              No activity recorded yet.
            </div>
          )}
          {!loading && !error && entries.length > 0 && (
            <ol className="relative border-l-2 border-gray-200 ml-3 space-y-5" data-testid="project-history-list">
              {entries.map((e) => {
                const meta = ACTION_META[e.action] || ACTION_META.updated;
                const Icon = meta.Icon;
                return (
                  <li key={e.id} className="ml-6" data-testid="project-history-entry">
                    <span className={`absolute -left-3 flex items-center justify-center w-6 h-6 rounded-full ring-4 ring-white ${meta.tone}`}>
                      <Icon size={12} />
                    </span>
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded ${meta.tone}`}>
                          {meta.label}
                        </span>
                        <span className="text-xs text-gray-500 flex items-center gap-1">
                          <Clock size={12} />{fmtDate(e.timestamp)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-600 flex items-center gap-1 mb-2">
                        <UserIcon size={12} />
                        <span className="font-medium text-gray-800">{e.userName || 'System'}</span>
                        {e.userEmail ? <span className="text-gray-400">· {e.userEmail}</span> : null}
                      </div>
                      {e.note && (
                        <p className="text-sm text-gray-700 mb-2">{e.note}</p>
                      )}
                      {Array.isArray(e.changes) && e.changes.length > 0 && (
                        <div className="divide-y divide-gray-200 mt-2">
                          {e.changes.map((c, idx) => (
                            <div key={idx} className="py-1.5 text-sm grid grid-cols-12 gap-2 items-start">
                              <div className="col-span-3 font-semibold text-gray-700">{c.label}</div>
                              <div className="col-span-4 text-red-700 break-words">{fmtValue(c.oldValue)}</div>
                              <div className="col-span-1 text-center text-gray-400"><ArrowRight size={13} className="inline" /></div>
                              <div className="col-span-4 text-green-700 break-words">{fmtValue(c.newValue)}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
